import React, { useState, useEffect } from 'react';
import { Send, Sparkles, Check, ArrowRight, X } from 'lucide-react';

/**
 * Manifesto Onboarding Component
 * M.ai.ry-guided conversation to help users create their personal manifesto
 * Natural, conversational approach - not overly "AI-hyped"
 */
const ManifestoOnboarding = ({ onComplete, onSkip }) => {
  const [conversation, setConversation] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [manifestoData, setManifestoData] = useState({
    purpose: '',
    values: [],
    skills: [],
    interests: [],
    short_term_goals: [],
    long_term_goals: []
  });
  const [currentStep, setCurrentStep] = useState(0);

  const initialGreeting = `Welcome! I'm M.ai.ry, and I'm here to help you create your personal manifesto.

Think of this as a conversation to help you articulate who you are, what you care about, and what you're here to do within our Federation.

There's no rush, and you can always come back to this later. Ready to begin?`;

  useEffect(() => {
    // Start with M.ai.ry's greeting
    setConversation([
      {
        role: 'assistant',
        content: initialGreeting,
        timestamp: new Date()
      }
    ]);
  }, []);

  const sendMessage = async () => {
    if (!userInput.trim() || isLoading) return;

    const newUserMessage = {
      role: 'user',
      content: userInput,
      timestamp: new Date()
    };

    setConversation(prev => [...prev, newUserMessage]);
    setUserInput('');
    setIsLoading(true);

    try {
      // Get backend URL
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('auth_token');

      const response = await fetch(`${backendUrl}/api/mairy/manifesto-guidance`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          message: userInput,
          history: conversation.map(msg => ({
            role: msg.role,
            content: msg.content
          })),
          current_step: currentStep
        })
      });

      if (response.ok) {
        const data = await response.json();
        
        const mairyResponse = {
          role: 'assistant',
          content: data.response,
          timestamp: new Date()
        };

        setConversation(prev => [...prev, mairyResponse]);
        setCurrentStep(prev => prev + 1);
      } else {
        throw new Error('Failed to get response');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setConversation(prev => [...prev, {
        role: 'assistant',
        content: "I'm having a bit of trouble connecting right now. Let's try that again?",
        timestamp: new Date()
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const saveManifesto = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('auth_token');

      // Extract manifesto data from conversation
      const conversationText = conversation
        .filter(msg => msg.role === 'user')
        .map(msg => msg.content)
        .join('\n\n');

      const response = await fetch(`${backendUrl}/api/user/manifesto`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          manifesto_data: {
            ...manifestoData,
            full_conversation: conversationText,
            created_at: new Date().toISOString()
          },
          is_complete: true,
          sharing_permissions: {
            matchmaking: true,
            lore: false
          }
        })
      });

      if (response.ok) {
        onComplete(manifestoData);
      } else {
        throw new Error('Failed to save manifesto');
      }
    } catch (error) {
      console.error('Error saving manifesto:', error);
      alert('There was an error saving your manifesto. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1E2A38] via-[#2d1e3a] to-[#1E2A38] relative overflow-hidden">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 20% 50%, #5C2D73 0%, transparent 50%),
                           radial-gradient(circle at 80% 80%, #E6C685 0%, transparent 50%)`
        }}></div>
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="mb-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Sparkles className="w-8 h-8 text-[#E6C685]" />
            <h1 
              className="text-4xl font-light tracking-wide text-transparent bg-clip-text bg-gradient-to-r from-[#E6C685] to-[#5C2D73]"
              style={{ fontFamily: "'Cinzel', serif" }}
            >
              Your Personal Manifesto
            </h1>
            <Sparkles className="w-8 h-8 text-[#E6C685]" />
          </div>
          <p className="text-[#CED3CC] text-lg font-light">
            A guided conversation with M.ai.ry
          </p>
        </div>

        {/* Action Buttons */}
        <div className="mb-6 flex justify-between items-center">
          <button
            onClick={onSkip}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-[#CED3CC]/30 
              text-[#CED3CC] hover:border-[#CED3CC] hover:bg-white/5 transition-all"
          >
            <X className="w-4 h-4" />
            Skip for now
          </button>
          
          {conversation.length > 4 && (
            <button
              onClick={saveManifesto}
              className="flex items-center gap-2 px-6 py-2 rounded-lg bg-gradient-to-r from-[#5C2D73] to-[#E6C685] 
                text-white hover:shadow-lg hover:scale-105 transition-all"
            >
              <Check className="w-5 h-5" />
              Complete Manifesto
            </button>
          )}
        </div>

        {/* Conversation Container */}
        <div 
          className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6 mb-6"
          style={{ 
            maxHeight: '500px', 
            overflowY: 'auto',
            boxShadow: '0 8px 32px 0 rgba(92, 45, 115, 0.37)'
          }}
        >
          {conversation.map((message, index) => (
            <div
              key={index}
              className={`mb-6 flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] rounded-2xl p-4 ${
                  message.role === 'user'
                    ? 'bg-gradient-to-r from-[#5C2D73]/40 to-[#E6C685]/40 border border-[#E6C685]/30'
                    : 'bg-white/10 border border-white/20'
                }`}
              >
                {message.role === 'assistant' && (
                  <div className="flex items-center gap-2 mb-2 text-[#E6C685] text-sm font-light">
                    <Sparkles className="w-4 h-4" />
                    M.ai.ry
                  </div>
                )}
                <p 
                  className="text-[#CED3CC] leading-relaxed whitespace-pre-wrap"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  {message.content}
                </p>
                <div className="text-xs text-[#CED3CC]/50 mt-2">
                  {message.timestamp.toLocaleTimeString()}
                </div>
              </div>
            </div>
          ))}

          {isLoading && (
            <div className="flex items-center gap-3 text-[#E6C685]">
              <Sparkles className="w-5 h-5 animate-pulse" />
              <span className="text-sm font-light">M.ai.ry is thinking...</span>
            </div>
          )}
        </div>

        {/* Input Area */}
        <div 
          className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-4"
          style={{ boxShadow: '0 8px 32px 0 rgba(92, 45, 115, 0.37)' }}
        >
          <div className="flex gap-3">
            <textarea
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Share your thoughts..."
              disabled={isLoading}
              className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-[#CED3CC] 
                placeholder-[#CED3CC]/50 focus:outline-none focus:border-[#E6C685] transition-all resize-none"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
              rows="3"
            />
            <button
              onClick={sendMessage}
              disabled={!userInput.trim() || isLoading}
              className="px-6 bg-gradient-to-r from-[#5C2D73] to-[#E6C685] text-white rounded-xl 
                hover:shadow-lg hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed 
                flex items-center justify-center"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
          <div className="mt-2 text-xs text-[#CED3CC]/50 text-center">
            Press Enter to send • Shift+Enter for new line
          </div>
        </div>

        {/* Progress Indicator */}
        {conversation.length > 2 && (
          <div className="mt-6 text-center">
            <div className="text-[#E6C685] text-sm font-light mb-2">
              Conversation Progress
            </div>
            <div className="flex justify-center gap-2">
              {[...Array(5)].map((_, i) => (
                <div
                  key={i}
                  className={`h-1 w-12 rounded-full transition-all ${
                    i < Math.min(Math.floor(conversation.length / 2), 5)
                      ? 'bg-[#E6C685]'
                      : 'bg-white/20'
                  }`}
                />
              ))}
            </div>
            <div className="text-xs text-[#CED3CC]/50 mt-2">
              {conversation.length > 10 
                ? "Great! Feel free to complete whenever you're ready."
                : "The more you share, the better we can help you connect with your people."}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManifestoOnboarding;
